import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatExchangeName } from '../utils/exchangeDisplay';
import './Currencies.css';

const Currencies = () => {
  const { user, updateUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [exchanges, setExchanges] = useState([]);
  const [activeTab, setActiveTab] = useState(searchParams.get('exchange') || '');
  const [currenciesByExchange, setCurrenciesByExchange] = useState({});
  const [exchangeSettings, setExchangeSettings] = useState({});
  const [fundingRates, setFundingRates] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasPosition, setHasPosition] = useState(false);
  const [registeredExchanges, setRegisteredExchanges] = useState([]);
  const [isTestnet, setIsTestnet] = useState(false);

  useEffect(() => {
    loadData();
    fetchTradingMode();
  }, []);

  const fetchTradingMode = async () => {
    try {
      const res = await api.get('/trading/trading-mode');
      setIsTestnet(res.data.mode === 'testnet');
    } catch (e) {
      // ignore
    }
  };

  const loadData = async () => {
    try {
      // 登録済みAPIキーの取引所を取得
      const apiKeyRes = await api.get('/apikey');
      const registered = apiKeyRes.data.apiKeys
        ? apiKeyRes.data.apiKeys.map(k => k.exchange)
        : apiKeyRes.data.hasApiKey ? [apiKeyRes.data.exchange || 'hyperliquid'] : [];
      setRegisteredExchanges(registered);

      // 利用可能通貨一覧
      const currRes = await api.get('/trading/currencies');
      const allCurrencies = currRes.data.currencies || {};
      setCurrenciesByExchange(allCurrencies);

      // 通貨定義のある全取引所をタブ表示
      const availableExchanges = Object.keys(allCurrencies);
      setExchanges(availableExchanges);
      if (availableExchanges.length > 0) {
        const fromUrl = searchParams.get('exchange');
        if (fromUrl && availableExchanges.includes(fromUrl)) {
          setActiveTab(fromUrl);
        } else if (!activeTab) {
          // 登録済み取引所を優先的に初期選択
          const firstRegistered = availableExchanges.find(ex => registered.includes(ex));
          const initial = firstRegistered || availableExchanges[0];
          setActiveTab(initial);
          setSearchParams({ exchange: initial }, { replace: true });
        }
      }

      // 現在の設定
      const settingsRes = await api.get('/trading/exchange-settings');
      const settingsMap = {};
      (settingsRes.data.exchangeSettings || []).forEach(s => {
        settingsMap[s.exchange] = s.activePair;
      });
      setExchangeSettings(settingsMap);

      // ファンディングレート取得
      if (registered.length > 0) {
        fetchFundingRates(allCurrencies, registered);
      }
    } catch (error) {
      console.error('Load data error:', error);
    }
  };

  const fetchFundingRates = async (allCurrencies, registered) => {
    const rates = {};
    for (const ex of registered) {
      const currencies = allCurrencies[ex] || [];
      for (const c of currencies) {
        try {
          const res = await api.get(`/trading/funding-rate/${c.tradingSymbol}?exchange=${ex}`);
          rates[`${ex}:${c.symbol}`] = res.data.fundingRate;
        } catch {
          rates[`${ex}:${c.symbol}`] = null;
        }
      }
    }
    setFundingRates(rates);
  };

  const selectPair = async (symbol) => {
    const current = exchangeSettings[activeTab];
    // トグル: 同じペアをクリックしたら解除
    const newPair = current === symbol ? null : symbol;

    setLoading(true);
    try {
      const res = await api.post('/trading/exchange-settings', {
        exchange: activeTab,
        activePair: newPair
      });
      const settingsMap = {};
      (res.data.exchangeSettings || []).forEach(s => {
        settingsMap[s.exchange] = s.activePair;
      });
      setExchangeSettings(settingsMap);
      updateUser({ exchangeSettings: res.data.exchangeSettings });
      toast.success(newPair ? `${newPair}を設定しました` : 'ペアを解除しました');
    } catch (error) {
      toast.error(error.response?.data?.message || '設定に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const formatFundingRate = (rate) => {
    if (rate === null || rate === undefined) return '-';
    const percentage = (rate * 100).toFixed(4);
    return `${rate >= 0 ? '+' : ''}${percentage}%`;
  };

  const currentCurrencies = currenciesByExchange[activeTab] || [];
  const activePair = exchangeSettings[activeTab] || null;
  const isRegistered = registeredExchanges.includes(activeTab);

  return (
    <div className="currencies">
      <div className="page-header">
        <h1>通貨設定</h1>
      </div>

      <div className="currency-info">
        <p>
          取引所ごとにデルタニュートラルで運用するペアを1つ選択してください。
          ポジション保持中はペアを変更できません。
        </p>
      </div>

      {exchanges.length === 0 ? (
        <div className="no-exchange">
          <p>利用可能な取引所がありません。</p>
        </div>
      ) : (
        <>
          <div className="exchange-tabs">
            {exchanges.map(ex => {
              const registered = registeredExchanges.includes(ex);
              return (
                <button
                  key={ex}
                  className={`exchange-tab ${activeTab === ex ? 'active' : ''} ${!registered ? 'unregistered' : ''}`}
                  onClick={() => {
                    setActiveTab(ex);
                    setSearchParams({ exchange: ex });
                  }}
                >
                  {formatExchangeName(ex)}
                  {exchangeSettings[ex] && (
                    <span className="tab-badge">{exchangeSettings[ex]}</span>
                  )}
                </button>
              );
            })}
          </div>

          {isTestnet && activeTab === 'aster' ? (
            <div className="no-apikey-notice">
              <p>Asterではテストモードで取引できません</p>
            </div>
          ) : !isRegistered ? (
            <div className="no-apikey-notice">
              <p>この取引所のAPIキーが登録されていません。アカウント設定からAPIキーを登録してください。</p>
            </div>
          ) : (
            <div className="currency-grid">
              {currentCurrencies.map((currency) => {
                const isActive = activePair === currency.symbol;
                const isOther = activePair && !isActive;
                const rateKey = `${activeTab}:${currency.symbol}`;
                return (
                  <div
                    key={currency.symbol}
                    className={`currency-card ${isActive ? 'enabled' : ''} ${isOther ? 'dimmed' : ''}`}
                    onClick={() => !loading && selectPair(currency.symbol)}
                  >
                    <div className="currency-header">
                      <span className="currency-symbol">{currency.symbol}</span>
                      <div className={`radio-indicator ${isActive ? 'selected' : ''}`}>
                        {isActive && <div className="radio-dot"></div>}
                      </div>
                    </div>
                    <div className="currency-body">
                      <span className="currency-name">{currency.name}</span>
                      <span className="currency-pair">{currency.tradingSymbol}</span>
                    </div>
                    <div className="currency-footer">
                      <span className="funding-label">FR</span>
                      <span className={`funding-rate ${
                        fundingRates[rateKey] == null
                          ? ''
                          : fundingRates[rateKey] > 0
                            ? 'positive'
                            : fundingRates[rateKey] < 0
                              ? 'negative'
                              : ''
                      }`}>
                        {formatFundingRate(fundingRates[rateKey])}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {activeTab === 'hyperliquid' && (
        <div className="info-section">
          <h2>入金方法</h2>
          <ul>
            <li>入金通貨はUSDC（Arbitrum）のみ対応</li>
            <li>Hyperliquidは統合口座（Unified Account）のため、入金したUSDCからスポット・パープ両方の取引が可能</li>
            <li>口座間の振替は不要（1回の入金で全取引に利用可能）</li>
            <li><strong>最低入金額: 25 USDC</strong>（自動エントリーにはspot買い + perpショート双方の最低注文額を満たす必要があります）</li>
          </ul>
        </div>
      )}

      {activeTab === 'aster' && !isTestnet && (
        <div className="info-section">
          <h2>入金方法</h2>
          <ul>
            <li>入金通貨は<strong>USDT</strong></li>
            <li>Aster DEXの入金方法に従ってください</li>
            <li>入金後、<strong>スポット口座とパーペチュアル口座でUSDTが50%ずつ</strong>となるように転送してください</li>
            <li><strong>最低入金額: 30 USDT（各口座に15 USDT以上）</strong> — Asterは口座が分離しているため、スポット・パーペチュアル各口座に最低注文額以上の残高が必要です</li>
          </ul>
        </div>
      )}

      {activeTab === 'bitget' && (
        <div className="info-section">
          <h2>入金方法</h2>
          <ul>
            <li>入金通貨は<strong>USDT</strong>。Spot ウォレットに入金してください</li>
            <li><strong>最低入金額: 25 USDT</strong>（自動エントリーにはspot買い + Coin-Mショート双方の最低注文額を満たす必要があります）</li>
            <li>残りの spot 買い・口座間振替・Coin-M ショートは <strong>本サービスが自動実行</strong>します (手動振替は不要)</li>
            <li><strong>Classic アカウント (UTA未切替) 必須</strong>。UTA に切替済の場合は Classic にダウングレードしてから利用</li>
            <li>※ <strong>総合アカウント (UTA / 統合トレーディングアカウント) は本サービス未対応</strong>。Bitget v3 UTA Coin-M API の挙動が確定しておらず、Classic アカウントのみで運用してください</li>
            <li>API キー発行時の 3 要素: ① <strong>APIキー</strong> (取引所発行) ② <strong>APIシークレット</strong> (作成時のみ表示、必ずメモ) ③ <strong>Passphrase</strong> (自分で設定、取引所からは発行されない)</li>
            <li>必要権限: <strong>現物取引 (Spot Trade) / 先物取引 (Futures, Coin-M 含む) / ウォレット振替 (Wallet Transfer)</strong> の 3 つを Read+Write で有効化</li>
            <li>※ Bitget Classic は Demo Trading (paptrading) 非対応のため mainnet 前提</li>
          </ul>
        </div>
      )}

      {activeTab === 'bitget_uta' && (
        <div className="info-section">
          <h2>入金方法</h2>
          {isTestnet ? (
            <ul>
              <li>Demo Trading画面で<strong>体験金（デモUSDT）を受け取る</strong></li>
              <li>受領したUSDTを<strong>総合アカウント</strong>に振替</li>
              <li>API管理画面（Demo Trading側）で<strong>パスフレーズを自分で入力</strong>。作成完了時に<strong>APIキーとシークレット</strong>を取得</li>
              <li><strong>シークレットとパスフレーズは作成後に画面で確認できない</strong>ので必ず記録しておく</li>
            </ul>
          ) : (
            <ul>
              <li>入金通貨は<strong>USDT</strong></li>
              <li>事前に Bitget アカウントを <strong>UTA（統合トレーディングアカウント）にアップグレード</strong>する必要あり</li>
              <li>UTA 有効時は単一プールから <strong>spot / Coin-M perp の両取引</strong>を自動引出 — 口座間振替は不要</li>
              <li>API管理画面でAPIキー作成時、<strong>Passphraseを自分で設定</strong>（取引所からは発行されない）</li>
              <li>必要権限: Spot trade（読み取り＋取引） + Futures trade（Coin-M 含む、読み取り＋取引）</li>
              <li><strong>最低入金額: 25 USDT</strong>（自動エントリーにはspot買い + Coin-Mショート双方の最低注文額を満たす必要があります）</li>
            </ul>
          )}
        </div>
      )}

      {activeTab === 'hyperliquid' && (
        <div className="info-section">
          <h2>取引方法</h2>
          <ul>
            <li>Hyperliquidは<strong>統合アカウント（Unified Account）</strong>を採用</li>
            <li>単一プール（USDC）から<strong>spot買い + perpショート</strong>を同時実行してデルタニュートラルを構築</li>
            <li>口座間の資金振替は不要 — 入金したUSDCがそのままspot/perp両方の証拠金として使用される</li>
            <li>建玉時にspot側でUSDC → コインに転換、perp側で同数量のショートを発注</li>
          </ul>
        </div>
      )}

      {activeTab === 'bitget' && (
        <div className="info-section">
          <h2>取引方法</h2>
          <ul>
            <li>Bitget <strong>Classic + Coin-M Inverse</strong> 方式（USDT 起点 → 現物担保ショート）</li>
            <li>建玉フロー (本サービスが自動実行):
              <ol style={{margin: '6px 0 0 18px', padding: 0}}>
                <li>Spot で USDT → BTC を買い (約 95% 投入)</li>
                <li>BTC を <strong>Spot 口座 → Coin-M 口座へ振替</strong></li>
                <li>その BTC を margin として <strong>Coin-M Inverse Short (BTCUSD)</strong> を 1 倍で発注</li>
              </ol>
            </li>
            <li>決済フロー: ① Coin-M Short Close → ② Coin-M → Spot 振替 → ③ Spot で BTC → USDT 売却 (USDT に戻る)</li>
            <li>Coin-M Inverse は FR がプラス側に偏るため、USDT-M より約 +1.5%/年有利 (実測ベース)</li>
            <li>往復手数料: spot 0.10%×2 + perp 0.06%×2 = <strong>約 0.32%</strong></li>
            <li>⚠ <strong>ポジションモードは「一方向 (one-way)」必須</strong>。両建て (hedge mode) のままだとエラーとなり注文失敗します</li>
            <li>設定場所: Bitget の <strong>Coin-M Futures (BTCUSD) ページ → 設定アイコン (⚙️) → ポジションモード → 一方向 / One-way Mode</strong> を選択 (※ 既存ポジションがあると変更不可、その場合はクローズしてから)</li>
          </ul>
        </div>
      )}

      {activeTab === 'bitget_uta' && (
        <div className="info-section">
          <h2>取引方法</h2>
          <ul>
            <li>Bitget <strong>UTA + Coin-M Inverse</strong> 方式（現物担保ショート）</li>
            <li>建玉フロー: ① Spot Buy BTC（USDT→BTC）→ ② Coin-M Inverse Short (BTCUSD) を発注（BTC が UTA cross-collateral で自動 margin 化）</li>
            <li>決済フロー: ① Coin-M Short Close → ② Spot Sell（BTC→USDT）</li>
            <li><strong>口座間 Transfer 不要</strong> — UTA の cross-collateral により Spot/Coin-M が単一プールで連動</li>
            <li>FR は Coin-M (BTCUSD インバース) 市場のもの（USDT-M より大幅に有利）</li>
            <li>※ Classic モードでは動作しません。事前に UTA へアップグレードが必要</li>
          </ul>
        </div>
      )}

      {activeTab === 'aster' && (
        <div className="info-section">
          <h2>取引方法</h2>
          <ul>
            <li>Asterは<strong>分離アカウント方式</strong> — Spotウォレットと Perpウォレットが別建て</li>
            <li>建玉時にspotウォレット側でコインを買い、perpウォレット側でショートを発注 — 双方に必要分のUSDTを事前配分する必要がある</li>
            <li>HyperliquidやBitget UTAと比べ、<strong>spot保有資産がperp担保に自動充当されない</strong>ため資金効率は約半分（USDT × 2倍が必要）</li>
          </ul>
        </div>
      )}

      <div className="info-section">
        <h2>取引ルール</h2>
        <ul>
          <li>レバレッジは1倍（フル担保）で固定</li>
          <li>1時間ごとにファンディングレートをチェックし、プラスの場合にデルタニュートラルポジションを自動構築（FR徴収サイクルは取引所により異なる: Hyperliquid 1時間 / Bitget・Aster 8時間）</li>
          <li>1つの取引所につき1ペアのみ有効化可能</li>
          <li>ポジション保持中はペア変更不可</li>
          <li>ファンディングフィーによる実現利益は口座残高に加算され、次回取引時に再投資（複利運用）</li>
          <li><strong>自動決済条件: エントリー価格から +50% 上昇したら自動でクローズ</strong>。価格高騰時にポジションを長時間放置しないための保険機構 (毎時 00 分に判定)</li>
        </ul>
      </div>
    </div>
  );
};

export default Currencies;
